import { useState } from 'react'
import { Alert, ConfirmDialog, Select, Textarea } from 'armxlib'
import { useToast } from '../../app/toast'
import { en } from '../../i18n/en'
import { ApiError } from '../../lib/api'
import type { Author, Message } from './api'
import { useBan, useDeleteMessage, useReport } from './hooks'

const t = en.chat

function errorText(error: unknown): string | null {
  if (!error) return null
  return error instanceof ApiError ? en.errors[error.code] : en.errors.internal
}

/** Props of {@link ReportDialog}. */
export interface ReportDialogProps {
  /** Message to report; the dialog is closed while `null`. */
  message: Message | null
  /** Called when the dialog closes, sent or not. */
  onClose: () => void
}

/** Asks for a reason, then reports the message. */
export function ReportDialog({ message, onClose }: ReportDialogProps) {
  const [reason, setReason] = useState('')
  const report = useReport()
  const toast = useToast()

  function close() {
    setReason('')
    report.reset()
    onClose()
  }

  return (
    <ConfirmDialog
      open={message !== null}
      title={t.reportTitle}
      description={t.reportDescription}
      confirmLabel={t.reportConfirm}
      cancelLabel={en.common.cancel}
      confirmDisabled={reason.trim() === ''}
      loading={report.isPending}
      onCancel={close}
      onConfirm={() =>
        message &&
        report.mutate(
          { messageId: message.id, reason: reason.trim() },
          {
            onSuccess: () => {
              toast(t.reported)
              close()
            },
          },
        )
      }
    >
      {report.error && <Alert tone="danger">{errorText(report.error)}</Alert>}
      <Textarea label={en.common.reason} maxLength={500} value={reason} onChange={(event) => setReason(event.target.value)} />
    </ConfirmDialog>
  )
}

/** Props of {@link DeleteDialog}. */
export interface DeleteDialogProps {
  /** Message to delete; the dialog is closed while `null`. */
  message: Pick<Message, 'id'> | null
  /** Called when the dialog closes, deleted or not. */
  onClose: () => void
}

/** Confirms, then deletes a message for everyone (moderators). */
export function DeleteDialog({ message, onClose }: DeleteDialogProps) {
  const remove = useDeleteMessage()
  const toast = useToast()

  function close() {
    remove.reset()
    onClose()
  }

  return (
    <ConfirmDialog
      open={message !== null}
      tone="danger"
      title={t.deleteTitle}
      description={t.deleteDescription}
      confirmLabel={t.delete}
      cancelLabel={en.common.cancel}
      loading={remove.isPending}
      onCancel={close}
      onConfirm={() =>
        message &&
        remove.mutate(message.id, {
          onSuccess: () => {
            toast(t.deleted)
            close()
          },
        })
      }
    >
      {remove.error && <Alert tone="danger">{errorText(remove.error)}</Alert>}
    </ConfirmDialog>
  )
}

/** Ban lengths offered to moderators. */
export const BAN_DURATIONS = {
  '1h': 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
  '30d': 30 * 24 * 60 * 60_000,
  forever: null,
} as const

type Duration = keyof typeof BAN_DURATIONS

/** Props of {@link BanDialog}. */
export interface BanDialogProps {
  /** User to ban; the dialog is closed while `null`. */
  user: Pick<Author, 'id' | 'username'> | null
  /** Called when the dialog closes, banned or not. */
  onClose: () => void
}

/** Asks for a duration and a reason, then bans the user (moderators). */
export function BanDialog({ user, onClose }: BanDialogProps) {
  const [duration, setDuration] = useState<Duration>('24h')
  const [reason, setReason] = useState('')
  const ban = useBan()
  const toast = useToast()

  function close() {
    setReason('')
    setDuration('24h')
    ban.reset()
    onClose()
  }

  function confirm() {
    if (!user) return
    const length = BAN_DURATIONS[duration]
    ban.mutate(
      { userId: user.id, reason: reason.trim(), until: length === null ? null : new Date(Date.now() + length) },
      {
        onSuccess: () => {
          toast(t.banned_toast)
          close()
        },
      },
    )
  }

  return (
    <ConfirmDialog
      open={user !== null}
      tone="danger"
      title={t.banTitle(user?.username ?? '')}
      description={t.banDescription}
      confirmLabel={t.banConfirm}
      cancelLabel={en.common.cancel}
      confirmDisabled={reason.trim() === ''}
      loading={ban.isPending}
      onCancel={close}
      onConfirm={confirm}
    >
      {ban.error && <Alert tone="danger">{errorText(ban.error)}</Alert>}
      <Select
        label={t.banDuration}
        value={duration}
        onChange={(event) => setDuration(event.target.value as Duration)}
        options={(Object.keys(BAN_DURATIONS) as Duration[]).map((value) => ({ value, label: t.durations[value] }))}
      />
      <Textarea
        label={en.common.reason}
        hint={t.banReasonHint}
        maxLength={500}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
    </ConfirmDialog>
  )
}
